import { NWLContext } from '../../../contexts';
import { useContext, useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { DateTimePicker } from '../Common/datetimepicker';
import dayjs, { Dayjs } from 'dayjs';
import Highcharts from 'highcharts';
import { getHistoricalReadings, getForecastReadings } from '@/services/readings';
import Chart, { extractForecastData } from '../Common/Chart/Chart';
import { Table, Dropdown, Button, Input } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import './comparisongraph.scss';
import { capitaliseWord } from '../../../utils';
import utc from 'dayjs/plugin/utc';
import { useActiveLocationId } from '@/components/utils/activeLocation';
import { nwlKeys } from '../../utils/Utils';

function getParentId(a: any) {
	return a?.measurementeInfraestructureId ?? a?.id ?? a?.measurementInfraestructureId;
}

function getParamId(a: any) {
	return (
		a?.measurementParameter?.paremeterId ??
		a?.measurementTarget?.measurementTargetId ??
		a?.measurementTarget?.id ??
		a?.mainMeasurementTarget?.measurementTargetId
	);
}

function getParamIdFromParameter(p: any): number | string | undefined {
	return p?.paremeterId ?? p?.parameterId ?? p?.measurementTargetId ?? p?.id;
}

function getParamLabelFromParameter(p: any): string {
	return (
		p?.paremeterTypeName ??
		p?.paremeterName ??
		p?.measurementTargetName ??
		p?.name ??
		p?.config?.type ??
		'Unknown parameter'
	);
}

dayjs.extend(utc);

const colorBlue = ['hsl(193,91%,31%)'];
const colorYellow = ['hsl(42,100%,68%)'];
const colorPurple = ['hsl(260,50%,56%)'];
const colorGreen = ['hsl(158,58%,48%)'];
const colorOrange = ['hsl(18,99%,65%)'];
const colorNavy = ['hsl(204,96%,20%)'];

const colorSet = [colorBlue, colorYellow, colorPurple, colorGreen, colorOrange, colorNavy];

type SearchCriteria = {
	text: string | null;
	assetType: FilterType | null;
};

type FilterType = {
	key: string;
	label: string;
};

type ForecastPoint = {
	time: number;
	value: number;
	iteration?: number;
};

const toForecastPoints = (members): ForecastPoint[] => {
	const flat: ForecastPoint[] = [];
	if (!Array.isArray(members)) return flat;

	members.forEach((member, iterationIndex) => {
		if (!member || typeof member !== 'object') return;

		Object.entries(member).forEach(([tsKey, rawValue]) => {
			if (rawValue == null) return;
			const tsNum = Number(tsKey);
			const vNum = Number(rawValue);

			if (!Number.isFinite(tsNum) || !Number.isFinite(vNum)) return;

			flat.push({
				time: tsNum,
				value: vNum,
				iteration: iterationIndex,
			});
		});
	});

	return flat;
};

const ModalHeader = ({ onModalClose }) => {
	return (
		<div className="flex items-center justify-between py-[24px] px-5 border-b border-b-[#c4ccd5]">
			<svg
				width="24"
				height="24"
				viewBox="0 0 24 24"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
				className="size-8"
			>
				<path
					d="M21 4.5H13.5V3C13.5 2.60218 13.342 2.22064 13.0607 1.93934C12.7794 1.65804 12.3978 1.5 12 1.5H3C2.60218 1.5 2.22064 1.65804 1.93934 1.93934C1.65804 2.22064 1.5 2.60218 1.5 3V18C1.5 18.3978 1.65804 18.7794 1.93934 19.0607C2.22064 19.342 2.60218 19.5 3 19.5H10.5V21C10.5 21.3978 10.658 21.7794 10.9393 22.0607C11.2206 22.342 11.6022 22.5 12 22.5H21C21.3978 22.5 21.7794 22.342 22.0607 22.0607C22.342 21.7794 22.5 21.3978 22.5 21V6C22.5 5.60218 22.342 5.22064 22.0607 4.93934C21.7794 4.65804 21.3978 4.5 21 4.5ZM3 11.25H7.6275L5.6925 13.1925L6.75 14.25L10.5 10.5L6.75 6.75L5.6925 7.8075L7.6275 9.75H3V3H12V18H3V11.25ZM12 21V19.5C12.3978 19.5 12.7794 19.342 13.0607 19.0607C13.342 18.7794 13.5 18.3978 13.5 18V6H21V12.75H16.3725L18.3075 10.8075L17.25 9.75L13.5 13.5L17.25 17.25L18.3075 16.1925L16.3725 14.25H21V21H12Z"
					fill="#535662"
				/>
			</svg>
			<h3 className="text-[24px] font-bold text-[#535662] ml-1">Compare asset parameters</h3>
			<button
				onClick={onModalClose}
				type="button"
				className="custom-modal-close-btn ms-auto"
				data-modal-hide="default-modal"
			>
				<svg
					className="w-3 h-3"
					aria-hidden="true"
					xmlns="http://www.w3.org/2000/svg"
					fill="none"
					viewBox="0 0 14 14"
				>
					<path
						stroke="currentColor"
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth="2"
						d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
					/>
				</svg>
				<span className="sr-only">Close modal</span>
			</button>
		</div>
	);
};

const GraphView = ({
	dateRange,
	setDateRange,
	yAxis,
	series,
	tableData,
	selectedAssets,
	selectedAssetItem,
	chartKey,
	onRemove,
	loading,
	error,
}: {
	dateRange: any;
	setDateRange: (dateRange: [Dayjs, Dayjs] | undefined) => void;
	yAxis: any[];
	series: any[];
	tableData: any[] | undefined;
	selectedAssets: any[];
	selectedAssetItem: any;
	chartKey: string;
	onRemove: (asset: any, parameterId: number) => void;
	loading: boolean;
	error: string | null;
}) => {
	const chartRef = useRef<Highcharts.Chart | null>(null);

	const setXAxisToRange = useCallback(() => {
		const chart = chartRef.current;
		if (!chart || !Array.isArray(chart.xAxis) || chart.xAxis.length === 0) return;
		if (!dateRange?.[0] || !dateRange?.[1]) return;

		const min = dayjs(dateRange[0]).valueOf();
		const max = dayjs(dateRange[1]).valueOf();

		chart.xAxis.forEach((xa) => xa.setExtremes(min, max, false));
		chart.update({ xAxis: { type: 'datetime', min, max, ordinal: false } }, false);
		chart.redraw();
	}, [dateRange]);

	const chartCallback = useCallback(
		(chart: Highcharts.Chart) => {
			chartRef.current = chart;
			setTimeout(() => setXAxisToRange(), 0);
		},
		[setXAxisToRange],
	);

	useEffect(() => {
		setXAxisToRange();
	}, [setXAxisToRange, series, chartKey]);

	return selectedAssets.length > 0 ? (
		<div className="flex flex-[2] flex-col bg-gray-200 h-full w-full p-3 overflow-y-auto">
			<div className="flex items-center justify-between px-3 pb-5">
				<div className="text-[#2B3A42] font-semibold">
					{`Comparing ${selectedAssets.length} of 6 asset parameters`}
				</div>
				{!loading && !error && (
					<div className="flex items-center gap-3">
						<DateTimePicker
							id="comparison-graph-date-time-picker"
							value={dateRange}
							allowClear={false}
							onCancel={() => {}}
							onSave={(dates: [Dayjs, Dayjs] | null) => {
								setDateRange(dates ?? undefined);
							}}
						/>
					</div>
				)}
			</div>

			<div className="relative">
				{!loading && (
					<Chart
						key={chartKey}
						yAxis={yAxis}
						series={series}
						showLegend={false}
						chartCallback={chartCallback}
					/>
				)}

				{loading && (
					<div className="absolute inset-0 bg-white/60 flex items-center justify-center z-10">
						<div className="flex items-center">
							<div className="h-6 w-6 rounded-full border-2 border-sky-300 border-t-sky-700 animate-spin" />
							<span className="ml-3 text-slate-700 font-semibold">Loading chart…</span>
						</div>
					</div>
				)}

				{!loading && error && (
					<div className="chart-overlay">
						<div>
							<svg
								width="222"
								height="198"
								viewBox="0 0 222 198"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M125.297 60.0142C113.956 67.3386 102.059 74.1793 89.3136 78.0739C76.5686 81.9685 63.4424 82.3912 50.5122 81.3552C42.0673 80.6799 34.735 80.5527 27.6546 85.7899C20.6681 90.7826 15.4683 97.8839 12.8217 106.047C8.6601 120.264 19.7736 134.556 30.3713 142.889C46.2574 155.358 70.5955 155.58 89.8463 153.54C112.111 151.198 133.79 144.257 154.176 135.225C172.12 127.273 194.142 115.75 201.497 96.1255C208.257 78.0808 197.598 53.5163 184.82 40.4759C181.213 36.5192 176.28 34.0192 170.951 33.4466C157.585 32.5937 146.83 45.7172 136.723 52.4641C132.976 54.9671 129.169 57.5269 125.297 60.0142Z"
									fill="white"
								/>
								<path
									opacity="0.5"
									d="M113.325 180.94C151.709 180.94 182.825 178.925 182.825 176.44C182.825 173.955 151.709 171.94 113.325 171.94C74.9414 171.94 43.8252 173.955 43.8252 176.44C43.8252 178.925 74.9414 180.94 113.325 180.94Z"
									fill="#9194A3"
								/>
								<path
									d="M57.7441 52.2139V145.714H159.244M76.2441 123.714L94.7441 97.7139L108.244 107.214L116.244 97.7139L130.244 107.214L155.244 68.7139"
									stroke="#66C8EB"
									stroke-width="9"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
							</svg>
							<div className="text-[18px] text-[#374151]">Unable to display graph</div>
							<div className="text-[12px] text-[#6B7280]">Please refresh or try again later.</div>
						</div>
					</div>
				)}
			</div>
			<div className="mt-5">
				<Table
					rowKey={(record) => {
						const paramId = getParamId(record);
						return `${record.id ?? record.measurementeInfraestructureId ?? 'asset'}:${paramId}`;
					}}
					dataSource={tableData}
					columns={[
						{
							title: 'Asset',
							dataIndex: 'name',
							render: (value, record) => {
								const colourIdx = record?._colorIndex ?? 0;
								const chip = colorSet[colourIdx % colorSet.length][0];
								return (
									<div className="flex flex-row gap-1 items-center">
										<div
											className="w-[20px] h-[20px] rounded-sm"
											style={{ backgroundColor: chip }}
										/>
										<span className="ml-1 text-[#023E65]">{value}</span>
									</div>
								);
							},
						},
						{
							title: 'Parameter',
							dataIndex: ['measurementTarget', 'measurementTargetName'],
							render: (__, record, _) => {
								return (
									<span className="text-[#525252]">
										{record.measurementParameter
											? record.measurementParameter?.paremeterTypeName
											: (record.measurementTarget?.measurementTargetName ??
												record.measurementTarget?.name ??
												'unknown')}
									</span>
								);
							},
						},
						{
							title: '',
							key: 'remove',
							align: 'right' as const,
							render: (_value, record) => {
								const paramId = getParamId(record);
								return (
									<button
										type="button"
										className="icon-pill remove-icon-pill"
										onClick={() => onRemove(record, paramId)}
										aria-label="Remove"
										id="remove-btn"
										data-testid={`${paramId}-remove-button-table`}
									>
										<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-5 h-5">
											<path
												d="M6 12h12"
												stroke="currentColor"
												strokeWidth="2"
												strokeLinecap="round"
											/>
											<circle
												cx="12"
												cy="12"
												r="16"
												stroke="currentColor"
												strokeWidth="2"
												fill="none"
											/>
										</svg>
									</button>
								);
							},
						},
					]}
					pagination={{ hideOnSinglePage: true }}
				/>
			</div>
		</div>
	) : (
		<div className="flex flex-[2] flex-col bg-gray-200 items-center justify-center">
			<svg
				width="221"
				height="197"
				viewBox="0 0 221 197"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
			>
				<path
					d="M124.507 59.3003C113.166 66.6247 101.269 73.4655 88.5235 77.3601C75.7786 81.2547 62.6523 81.6773 49.7222 80.6413C41.2772 79.966 33.945 79.8388 26.8645 85.0761C19.8781 90.0687 14.6783 97.17 12.0316 105.333C7.87006 119.55 18.9836 133.842 29.5813 142.175C45.4674 154.644 69.8054 154.867 89.0562 152.826C111.321 150.484 133 143.543 153.386 134.511C171.33 126.559 193.352 115.036 200.707 95.4117C207.467 77.367 196.808 52.8024 184.03 39.762C180.423 35.8054 175.49 33.3054 170.161 32.7327C156.795 31.8798 146.04 45.0033 135.933 51.7502C132.186 54.2532 128.379 56.8131 124.507 59.3003Z"
					fill="white"
				/>
				<path
					d="M74.169 87.25H30.4189C27.9325 87.25 25.548 88.2377 23.7898 89.9959C22.0317 91.754 21.0439 94.1386 21.0439 96.625V140.375C21.0439 142.861 22.0317 145.246 23.7898 147.004C25.548 148.762 27.9325 149.75 30.4189 149.75H74.169C76.6554 149.75 79.0399 148.762 80.7981 147.004C82.5562 145.246 83.544 142.861 83.544 140.375V96.625C83.544 94.1386 82.5562 91.754 80.7981 89.9959C79.0399 88.2377 76.6554 87.25 74.169 87.25ZM77.294 140.375C77.294 141.204 76.9647 141.999 76.3787 142.585C75.7926 143.171 74.9978 143.5 74.169 143.5H30.4189C29.5901 143.5 28.7953 143.171 28.2092 142.585C27.6232 141.999 27.2939 141.204 27.2939 140.375V96.625C27.2939 95.7962 27.6232 95.0013 28.2092 94.4153C28.7953 93.8292 29.5901 93.5 30.4189 93.5H74.169C74.9978 93.5 75.7926 93.8292 76.3787 94.4153C76.9647 95.0013 77.294 95.7962 77.294 96.625V140.375Z"
					fill="#66C8EB"
				/>
				<path
					d="M62.2662 108.528C62.0163 108.276 61.719 108.076 61.3915 107.939C61.0639 107.803 60.7126 107.733 60.3577 107.733C60.0029 107.733 59.6516 107.803 59.324 107.939C58.9965 108.076 58.6992 108.276 58.4493 108.528L52.2939 114.71L46.1386 108.528C45.6324 108.022 44.9459 107.737 44.2301 107.737C43.5143 107.737 42.8278 108.022 42.3217 108.528C41.8155 109.034 41.5312 109.72 41.5312 110.436C41.5312 111.152 41.8155 111.838 42.3217 112.345L48.504 118.5L42.3217 124.655C42.0698 124.905 41.8698 125.203 41.7333 125.53C41.5969 125.858 41.5266 126.209 41.5266 126.564C41.5266 126.919 41.5969 127.27 41.7333 127.598C41.8698 127.925 42.0698 128.222 42.3217 128.472C42.5716 128.724 42.8689 128.924 43.1964 129.061C43.524 129.197 43.8753 129.267 44.2301 129.267C44.585 129.267 44.9363 129.197 45.2638 129.061C45.5914 128.924 45.8887 128.724 46.1386 128.472L52.2939 122.29L58.4493 128.472C58.6992 128.724 58.9965 128.924 59.324 129.061C59.6516 129.197 60.0029 129.267 60.3577 129.267C60.7126 129.267 61.0639 129.197 61.3915 129.061C61.719 128.924 62.0163 128.724 62.2662 128.472C62.5181 128.222 62.7181 127.925 62.8545 127.598C62.991 127.27 63.0613 126.919 63.0613 126.564C63.0613 126.209 62.991 125.858 62.8545 125.53C62.7181 125.203 62.5181 124.905 62.2662 124.655L56.0839 118.5L62.2662 112.345C62.5181 112.095 62.7181 111.797 62.8545 111.47C62.991 111.142 63.0613 110.791 63.0613 110.436C63.0613 110.081 62.991 109.73 62.8545 109.402C62.7181 109.075 62.5181 108.778 62.2662 108.528Z"
					fill="#66C8EB"
				/>
				<path
					d="M190.169 87.25H146.419C143.933 87.25 141.548 88.2377 139.79 89.9959C138.032 91.754 137.044 94.1386 137.044 96.625V140.375C137.044 142.861 138.032 145.246 139.79 147.004C141.548 148.762 143.933 149.75 146.419 149.75H190.169C192.655 149.75 195.04 148.762 196.798 147.004C198.556 145.246 199.544 142.861 199.544 140.375V96.625C199.544 94.1386 198.556 91.754 196.798 89.9959C195.04 88.2377 192.655 87.25 190.169 87.25ZM193.294 140.375C193.294 141.204 192.965 141.999 192.379 142.585C191.793 143.171 190.998 143.5 190.169 143.5H146.419C145.59 143.5 144.795 143.171 144.209 142.585C143.623 141.999 143.294 141.204 143.294 140.375V96.625C143.294 95.7962 143.623 95.0013 144.209 94.4153C144.795 93.8292 145.59 93.5 146.419 93.5H190.169C190.998 93.5 191.793 93.8292 192.379 94.4153C192.965 95.0013 193.294 95.7962 193.294 96.625V140.375Z"
					fill="#66C8EB"
				/>
				<path
					d="M178.266 108.528C178.016 108.276 177.719 108.076 177.391 107.939C177.064 107.803 176.713 107.733 176.358 107.733C176.003 107.733 175.652 107.803 175.324 107.939C174.996 108.076 174.699 108.276 174.449 108.528L168.294 114.71L162.139 108.528C161.632 108.022 160.946 107.737 160.23 107.737C159.514 107.737 158.828 108.022 158.322 108.528C157.816 109.034 157.531 109.72 157.531 110.436C157.531 111.152 157.816 111.838 158.322 112.345L164.504 118.5L158.322 124.655C158.07 124.905 157.87 125.203 157.733 125.53C157.597 125.858 157.527 126.209 157.527 126.564C157.527 126.919 157.597 127.27 157.733 127.598C157.87 127.925 158.07 128.222 158.322 128.472C158.572 128.724 158.869 128.924 159.196 129.061C159.524 129.197 159.875 129.267 160.23 129.267C160.585 129.267 160.936 129.197 161.264 129.061C161.591 128.924 161.889 128.724 162.139 128.472L168.294 122.29L174.449 128.472C174.699 128.724 174.996 128.924 175.324 129.061C175.652 129.197 176.003 129.267 176.358 129.267C176.713 129.267 177.064 129.197 177.391 129.061C177.719 128.924 178.016 128.724 178.266 128.472C178.518 128.222 178.718 127.925 178.855 127.598C178.991 127.27 179.061 126.919 179.061 126.564C179.061 126.209 178.991 125.858 178.855 125.53C178.718 125.203 178.518 124.905 178.266 124.655L172.084 118.5L178.266 112.345C178.518 112.095 178.718 111.797 178.855 111.47C178.991 111.142 179.061 110.791 179.061 110.436C179.061 110.081 178.991 109.73 178.855 109.402C178.718 109.075 178.518 108.778 178.266 108.528Z"
					fill="#66C8EB"
				/>
				<rect x="74.7939" y="54" width="73" height="73" fill="white" />
				<path
					d="M139.752 48.4167H80.8357C77.4873 48.4167 74.2761 49.7469 71.9085 52.1145C69.5408 54.4822 68.2107 57.6934 68.2107 61.0417V119.958C68.2107 123.307 69.5408 126.518 71.9085 128.886C74.2761 131.253 77.4873 132.583 80.8357 132.583H139.752C143.101 132.583 146.312 131.253 148.68 128.886C151.047 126.518 152.377 123.307 152.377 119.958V61.0417C152.377 57.6934 151.047 54.4822 148.68 52.1145C146.312 49.7469 143.101 48.4167 139.752 48.4167ZM143.961 119.958C143.961 121.075 143.517 122.145 142.728 122.934C141.939 123.723 140.868 124.167 139.752 124.167H80.8357C79.7196 124.167 78.6492 123.723 77.86 122.934C77.0707 122.145 76.6274 121.075 76.6274 119.958V61.0417C76.6274 59.9256 77.0707 58.8552 77.86 58.066C78.6492 57.2768 79.7196 56.8334 80.8357 56.8334H139.752C140.868 56.8334 141.939 57.2768 142.728 58.066C143.517 58.8552 143.961 59.9256 143.961 61.0417V119.958Z"
					fill="#66C8EB"
				/>
				<path
					d="M104.148 103.18C103.635 102.966 103.17 102.653 102.778 102.258L94.6983 94.1785C93.9059 93.386 93.4607 92.3112 93.4607 91.1905C93.4607 90.0699 93.9059 88.9951 94.6983 88.2026C95.4908 87.4102 96.5656 86.965 97.6862 86.965C98.8069 86.965 99.8817 87.4102 100.674 88.2026L105.766 93.3368L119.948 79.1126C120.741 78.3202 121.816 77.875 122.936 77.875C124.057 77.875 125.132 78.3202 125.924 79.1126C126.717 79.9051 127.162 80.9799 127.162 82.1006C127.162 83.2212 126.717 84.296 125.924 85.0885L108.754 102.258C108.363 102.653 107.897 102.966 107.385 103.18C106.872 103.393 106.322 103.503 105.766 103.503C105.211 103.503 104.661 103.393 104.148 103.18Z"
					fill="#66C8EB"
				/>
				<path
					opacity="0.5"
					d="M112.535 180.226C150.919 180.226 182.035 178.211 182.035 175.726C182.035 173.241 150.919 171.226 112.535 171.226C74.1516 171.226 43.0354 173.241 43.0354 175.726C43.0354 178.211 74.1516 180.226 112.535 180.226Z"
					fill="#9194A3"
				/>
			</svg>
			<h4 className="text-2xl font-semibold text-[#535662]">No asset parameters selected</h4>
			<p>Select an asset parameter to compare</p>
		</div>
	);
};

const ListItem = ({
	index,
	name,
	measurementTypes,
	assetType,
	selectedParameterIds,
	onAdd,
	onRemove,
	maxSelected,
}) => {
	return (
		<div className="flex flex-col p-2 hover:bg-gray-100 cursor-pointer" id="asset-list">
			<div>
				<span className="text-[14px] font-semibold text-[#023E65]">{name}</span>
				{measurementTypes.map((measurementType, measurementTypeIndex) => {
					const isSelected = selectedParameterIds.includes(measurementType.paremeterId);
					const addDisabled = !isSelected && selectedParameterIds.length >= maxSelected;
					const rowKey = `${name}-${assetType}-${measurementType.paremeterId ?? measurementTypeIndex}`;
					return (
						<div
							key={rowKey}
							onClick={() =>
								isSelected
									? onRemove(measurementType.paremeterId)
									: onAdd(measurementType.paremeterId)
							}
						>
							<div className="flex justify-between items-center py-1">
								<span className="text-[12px] text-[#434B56]">
									{capitaliseWord(assetType.toLowerCase())} <span className="text-gray-300">|</span>{' '}
									{measurementType.paremeterTypeName}
								</span>
								{!isSelected ? (
									<button
										onClick={(e) => {
											e.stopPropagation();
											onAdd(measurementType.paremeterId);
										}}
										className="icon-pill disabled:opacity-40"
										disabled={addDisabled}
										id={`${measurementType.paremeterId}-add-button`}
										data-testid={`${measurementType.paremeterId}-add-button`}
									>
										<svg viewBox="0 0 24 24">
											<path
												d="M12 7v10M7 12h10"
												stroke="currentColor"
												strokeWidth="2"
												strokeLinecap="round"
											/>
											<circle
												cx="12"
												cy="12"
												r="9"
												stroke="currentColor"
												strokeWidth="2"
												fill="none"
											/>
										</svg>
									</button>
								) : (
									<button
										onClick={(e) => {
											e.stopPropagation();
											onRemove(measurementType.paremeterId);
										}}
										className="icon-pill remove-icon-pill"
										id={`${measurementType.paremeterId}-remove-button`}
										data-testid={`${measurementType.paremeterId}-remove-button`}
									>
										<svg viewBox="0 0 24 24">
											<path
												d="M7 12h10"
												stroke="currentColor"
												strokeWidth="2"
												strokeLinecap="round"
											/>
										</svg>
									</button>
								)}
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
};

const AssetListView = ({
	maxSelected,
	unselectedAssets,
	selectedAssets,
	onAdd,
	onRemove,
	onSearch,
	filterOptions,
	searchCriteria,
	onFilterSelected,
}) => {
	const selectedParameterIds = selectedAssets.map(getParamId);
	return (
		<div className="flex flex-[1] flex-col bg-white h-full w-full overflow-clip">
			<FilterPanel
				onSearch={onSearch}
				filterOptions={filterOptions}
				searchCriteria={searchCriteria}
				onFilterSelected={onFilterSelected}
			/>
			<div className="overflow-y-auto h-full">
				{unselectedAssets &&
					unselectedAssets.length > 0 &&
					unselectedAssets.map((asset, index) => {
						const assetKey = asset.measurementeInfraestructureId ?? asset.id ?? index;
						const measurementTypes = (
							asset?.measurementParameters?.map((p) => ({
								paremeterId: getParamIdFromParameter(p),
								paremeterTypeName: getParamLabelFromParameter(p),
							})) ??
							(asset?.mainMeasurementTarget
								? [
										{
											paremeterId: getParamIdFromParameter(asset.mainMeasurementTarget),
											paremeterTypeName: getParamLabelFromParameter(asset.mainMeasurementTarget),
										},
									]
								: [])
						).filter((mt) => mt.paremeterId != null);
						return (
							<div key={`asset-${assetKey}`}>
								<ListItem
									index={`unselected_asset_${assetKey}`}
									name={asset?.measurementInfraestructureName || 'unknown'}
									measurementTypes={measurementTypes}
									onAdd={(paremeterId: number) => onAdd(asset, paremeterId)}
									onRemove={(paremeterId: number) => onRemove(asset, paremeterId)}
									selectedParameterIds={selectedParameterIds}
									assetType={asset?.infrastructureTypeName}
									maxSelected={maxSelected}
								/>
							</div>
						);
					})}
			</div>
		</div>
	);
};

const FilterPanel = ({ onSearch, filterOptions, searchCriteria, onFilterSelected }) => {
	const sortedFilterOptions = useMemo(
		() =>
			[...filterOptions].sort((a, b) =>
				a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
			),
		[filterOptions],
	);

	return (
		<div className="p-3 border-b border-b-gray-300 border-w-[50%]">
			<div className="font-bold text-[14px] text-[#535662]">All assets</div>
			<div className="flex w-full mt-2">
				<div className="w-full pr-3">
					<Input
						data-testid="asset-text-filter"
						placeholder="search"
						allowClear
						size="large"
						prefix={
							<svg
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
								strokeWidth="1.5"
								stroke="currentColor"
								className="size-6"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
								/>
							</svg>
						}
						onChange={(e) => {
							onSearch(e.target.value);
						}}
					/>
				</div>
				<div className="w-full">
					<Dropdown
						menu={{
							items: sortedFilterOptions,
							onClick: ({ key }) =>
								onFilterSelected(sortedFilterOptions.find((opt) => opt.key === key)),
						}}
					>
						<Button className="w-full" data-testid="asset-type-filter">
							{searchCriteria?.assetType?.label ?? sortedFilterOptions[0].label}
							<DownOutlined />
						</Button>
					</Dropdown>
				</div>
			</div>
		</div>
	);
};

const ComparisonGraph = ({
	fetchHistoricalReadings = getHistoricalReadings,
	fetchForecastReadings = getForecastReadings,
	assets,
	allMapAssets,
}) => {
	const {
		decisionEngine: {
			showComparisonGraph,
			setShowComparisonGraph,
			dateRange,
			setDateRange,
			selectedAssetItem,
		},
	}: any = useContext(NWLContext);
	const [selectedAssets, setSelectedAssets] = useState<any[]>([]);
	const [yAxis, setYAxis] = useState<Highcharts.YAxisOptions[]>([]);
	const [series, setSeries] = useState<Highcharts.SeriesOptionsType[]>([]);
	const [filteredAssets, setFilteredAssets] = useState<any[] | null>(null);
	const [defaultAssets, setDefaultAssets] = useState<any[] | null>(null);
	const [typeFilter, setTypeFilter] = useState<any[] | null>([
		{ key: '0', label: 'All asset types' },
	]);
	const [searchCriteria, setSearchCriteria] = useState<SearchCriteria | null>(null);
	const [colorMap, setColorMap] = useState<Record<string, number>>({});
	const [pendingAdds, setPendingAdds] = useState<Record<string, boolean>>({});
	const fetchTokenRef = useRef(0);
	const [graphLoading, setGraphLoading] = useState(false);
	const [graphError, setGraphError] = useState<string | null>(null);
	const [refetchNonce, setRefetchNonce] = useState(0);
	const locationId = useActiveLocationId();

	useEffect(() => {
		if (!showComparisonGraph) return;
		setSelectedAssets([]);
		setSeries([]);
		setYAxis([]);
		setGraphError(null);
	}, [locationId, showComparisonGraph]);

	useEffect(() => {
		if (!dateRange || !showComparisonGraph) return;
		if (!selectedAssets || selectedAssets.length === 0) {
			setGraphLoading(false);
			setGraphError(null);
			setSeries([]);
			setYAxis([]);
			return;
		}
		const token = ++fetchTokenRef.current;
		setGraphError(null);
		setGraphLoading(true);

		const dateFrom = dateRange[0].unix().toString();
		const dateTo = dateRange[1].unix().toString();

		(async () => {
			try {
				const { seriesData, yAxisData } = await fetchGraphData(selectedAssets, dateFrom, dateTo);
				if (fetchTokenRef.current !== token) return;

				setYAxis(yAxisData);
				setSeries(seriesData);
				if (!seriesData.length) throw new Error('No data available to plot.');
			} catch (err: any) {
				if (fetchTokenRef.current !== token) return;
				setYAxis([]);
				setSeries([]);
				setGraphError(err?.message || 'Failed to load chart data.');
			} finally {
				if (fetchTokenRef.current === token) setGraphLoading(false);
			}
		})();
	}, [selectedAssets, dateRange, showComparisonGraph, refetchNonce, locationId]);

	const buildSelectionKey = (a: any) => {
		const parentId = getParentId(a);
		const paramId = getParamId(a);
		return `${parentId}:${paramId}`;
	};

	const chartKey = useMemo(() => {
		return selectedAssets
			.map((a) => buildSelectionKey(a))
			.sort()
			.join('|');
	}, [selectedAssets]);

	type XY = [number, number];

	const toXYHistoric = (obj: any): XY[] => {
		const out: XY[] = [];
		if (!obj || typeof obj !== 'object') return out;

		for (const k of Object.keys(obj)) {
			const ts = Number(k);
			const v = obj[k]?.v;
			if (Number.isFinite(ts) && Number.isFinite(v)) out.push([ts, Number(v)]);
		}
		out.sort((a, b) => a[0] - b[0]);
		return out;
	};

	const splitReadings = (data: any) => {
		const historic = toXYHistoric(data?.measurementToolHistoricalValues);
		const flatForecastPoints = toForecastPoints(data?.measurementToolForecastValues);
		const forecast = extractForecastData(flatForecastPoints);
		return { historic, forecast };
	};

	type JoinedReadings = {
		measurementToolHistoricalValues: Record<number, { v: number | null; s: string }>;
		measurementToolForecastValues: Record<number, number>[];
	};

	const buildJoinedReadings = async (
		asset: any,
		parameterId: number | string,
		dateFrom: string,
		dateTo: string,
	): Promise<JoinedReadings> => {
		const securityRaw = localStorage.getItem('security');
		const security = securityRaw ? JSON.parse(securityRaw) : {};
		const organisationId = security?.organizationId;

		if (!organisationId || !locationId) {
			throw new Error('No organization/locations found');
		}

		const serialNumber =
			asset.measurementParameter?.dataSources?.[0]?.serialNumber ??
			asset.measurementTarget?.dataSources?.[0]?.serialNumber ??
			asset.mainMeasurementTarget?.dataSources?.[0]?.serialNumber;

		if (!serialNumber) {
			throw new Error('No serial number found for asset parameter');
		}

		const storedDtId = (
			localStorage.getItem(nwlKeys.dtId(organisationId, locationId)) ?? ''
		).trim();
		const storedSimId = (
			localStorage.getItem(nwlKeys.simId(organisationId, locationId)) ?? ''
		).trim();

		const nowUnix = dayjs().unix().toString();
		const shouldFetchForecast = Boolean(storedDtId && storedSimId);

		const historicalPromise = fetchHistoricalReadings(
			organisationId,
			locationId,
			serialNumber,
			dateFrom,
			dateTo,
		);

		const forecastPromise = shouldFetchForecast
			? fetchForecastReadings(
					organisationId,
					locationId,
					parameterId,
					storedDtId,
					storedSimId,
					nowUnix,
					dateTo,
				)
			: Promise.resolve({ data: [] });

		const [historicalResponse, forecastResponse] = await Promise.all([
			historicalPromise,
			forecastPromise,
		]);

		const historicalArray: Array<{ time: number; value: number | null; source?: string }> =
			Array.isArray(historicalResponse?.data) ? historicalResponse.data : [];

		const forecastArray: Array<{ time: number; value: number | null; iteration?: number | null }> =
			Array.isArray(forecastResponse?.data) ? forecastResponse.data : [];

		const measurementToolHistoricalValues: Record<number, { v: number | null; s: string }> = {};
		for (const point of historicalArray) {
			if (!point || typeof point.time === 'undefined') continue;

			const tsNum = Number(point.time);
			if (!Number.isFinite(tsNum)) continue;
			const tsMs = tsNum > 1e12 ? tsNum : tsNum * 1000;

			measurementToolHistoricalValues[tsMs] = {
				v: point.value,
				s: point.source ?? 'NWL',
			};
		}

		// Group forecast values by iteration into the "members" array
		const membersByIteration = {};
		for (const point of forecastArray) {
			if (!point || typeof point.time === 'undefined') continue;

			if (point.value == null && point.iteration == null) {
				continue;
			}

			const tsNum = Number(point.time);
			if (!Number.isFinite(tsNum)) continue;
			const tsMs = tsNum > 1e12 ? tsNum : tsNum * 1000;

			if (point.value == null) continue;
			const vNum = Number(point.value);
			if (!Number.isFinite(vNum)) continue;

			const iterKey = String(point.iteration ?? 0);
			if (!membersByIteration[iterKey]) membersByIteration[iterKey] = {};
			membersByIteration[iterKey][tsMs] = vNum;
		}

		const measurementToolForecastValues: Record<number, number>[] =
			Object.values(membersByIteration);

		return { measurementToolHistoricalValues, measurementToolForecastValues };
	};

	const fetchGraphData = async (assets, dateFrom, dateTo) => {
		const seriesData: Highcharts.SeriesOptionsType[] = [];
		const yAxisData: Highcharts.YAxisOptions[] = [];
		const axisKeyToIndex: Record<string, number> = {};
		let axisCount = 0;

		for (let i = 0; i < assets.length; i++) {
			const asset = assets[i];
			const parameterId = getParamId(asset);
			const unit =
				asset?.measurementParameter?.config?.unit ??
				asset?.measurementTarget?.unit ??
				asset?.measurementTarget?.config?.unit ??
				asset?.mainMeasurementTarget?.unit ??
				'unknown';

			const label =
				asset?.measurementParameter?.paremeterTypeName ??
				asset?.measurementParameter?.paremeterName ??
				asset?.measurementTarget?.measurementTargetName ??
				asset?.measurementTarget?.config?.type ??
				asset?.mainMeasurementTarget?.measurementTargetName ??
				'unknown';

			if (!parameterId) continue;

			const axisKey = `${label}||${unit}`;
			let yAxisIndex = axisKeyToIndex[axisKey];
			if (yAxisIndex == null) {
				yAxisIndex = axisCount++;
				axisKeyToIndex[axisKey] = yAxisIndex;
				yAxisData.push({
					title: { text: `${label} (${unit})` },
					labels: {
						formatter: function () {
							return `${this.value}${unit}`;
						},
					},
					lineWidth: 1,
					opposite: yAxisIndex % 2 === 1,
				} as Highcharts.YAxisOptions);
			}

			const colourIndex: number = asset?._colorIndex ?? 0;
			const color = colorSet[colourIndex % colorSet.length][0];
			const selKey = `${asset?.id ?? asset?.measurementeInfraestructureId}:${parameterId}`;
			const nameBase = `${asset?.name ?? asset?.measurementInfraestructureName ?? 'Asset'} — ${label}`;

			try {
				const joined = await buildJoinedReadings(asset, parameterId, dateFrom, dateTo);

				const { historic, forecast } = splitReadings(joined);

				if (historic?.length) {
					seriesData.push({
						type: 'area',
						name: `${nameBase}`,
						data: historic,
						yAxis: yAxisIndex,
						lineWidth: 3,
						id: `${selKey}::historic`,
						color,
						dashStyle: 'Solid',
						marker: { enabled: false },
						zIndex: 1,
					} as Highcharts.SeriesOptionsType);
				}

				if (forecast?.length) {
					seriesData.push({
						type: 'line',
						name: `${nameBase} (Forecasted)`,
						data: forecast,
						yAxis: yAxisIndex,
						lineWidth: 3,
						id: `${selKey}::forecast`,
						color,
						dashStyle: 'ShortDot',
						marker: { enabled: false },
						zIndex: 2,
					} as Highcharts.SeriesOptionsType);
				}
			} catch {
				// ignore this asset if the joined fetch fails
				continue;
			}
		}
		return { seriesData, yAxisData };
	};

	useEffect(() => {
		if ((assets && assets.length > 0) || (allMapAssets && allMapAssets.length > 0)) {
			const infraById = new Map<string | number, any>();

			const addOrMerge = (key: string | number, base: any, extra?: any) => {
				const existing = infraById.get(key);
				const merged: any = {
					...(existing || {}),
					...(base || {}),
				};

				const measurementParamsFromExtra =
					extra?.measurementParameters ??
					extra?.parameters ??
					merged.measurementParameters ??
					existing?.measurementParameters ??
					[];

				if (measurementParamsFromExtra && measurementParamsFromExtra.length) {
					merged.measurementParameters = measurementParamsFromExtra;
				}

				if (!merged.measurementInfraestructureName && extra?.measurementInfraestructureName) {
					merged.measurementInfraestructureName = extra.measurementInfraestructureName;
				}
				if (!merged.infrastructureTypeName && extra?.infrastructureTypeName) {
					merged.infrastructureTypeName = extra.infrastructureTypeName;
				}

				infraById.set(key, merged);
			};

			assets?.forEach((a) => {
				const id = a.measurementeInfraestructureId ?? a.id ?? a.measurementInfraestructureId;
				if (id == null) return;
				addOrMerge(id, a);
			});

			allMapAssets?.forEach((m) => {
				const id = m.infraestructureId ?? m.measurementeInfraestructureId ?? m.id;
				if (id == null) return;

				const base = infraById.get(id) || {
					measurementeInfraestructureId: id,
					measurementInfraestructureName:
						m.measurementInfraestructureName ?? m.name ?? m.assetName ?? 'Unknown asset',
					infrastructureTypeName: m.infrastructureTypeName ?? m.type,
					mainMeasurementTarget: m.mainMeasurementTarget,
				};

				addOrMerge(id, base, m);
			});

			const list = Array.from(infraById.values());
			setDefaultAssets(list);
			setFilteredAssets(list);
		} else {
			setFilteredAssets(assets);
		}
	}, [assets, allMapAssets]);

	useEffect(() => {
		if (!selectedAssetItem || !showComparisonGraph) return;

		const parentId =
			selectedAssetItem?.measurementeInfraestructureId ??
			selectedAssetItem?.id ??
			selectedAssetItem?.measurementInfraestructureId;
		const parentParamId =
			selectedAssetItem?.measurementTarget?.measurementTargetId ??
			selectedAssetItem?.measurementTarget?.id ??
			selectedAssetItem?.mainMeasurementTarget?.measurementTargetId ??
			selectedAssetItem?.measurementParameter?.paremeterId;
		const selectionKey = `${String(parentId)}:${String(parentParamId)}`;

		if (!parentParamId) {
			setSelectedAssets([]);
			return;
		}

		setColorMap((prev) =>
			prev[selectionKey] !== undefined ? prev : { ...prev, [selectionKey]: 0 },
		);

		setSelectedAssets((prev) => {
			const alreadyPinned =
				prev.length === 1 &&
				getParentId(prev[0]) === parentId &&
				getParamId(prev[0]) === parentParamId;

			if (alreadyPinned) return prev;
			return [{ ...selectedAssetItem, _colorIndex: 0 }];
		});

		setSearchCriteria(null);
	}, [selectedAssetItem?.measurementTarget, showComparisonGraph, defaultAssets]);

	useEffect(() => {
		let filterOptions: any[] = [{ key: '0', label: 'All asset types' }];
		const source = defaultAssets ?? assets ?? [];

		if (source && source.length > 0) {
			const types: string[] = source
				.map((a: any) => a.infrastructureTypeName ?? a.type)
				.filter(Boolean);
			const uniqueTypes: string[] = [...new Set(types)];

			uniqueTypes.forEach((t, index) => {
				filterOptions.push({
					key: (index + 1).toString(),
					label: capitaliseWord(String(t).toLowerCase()),
				});
			});
		}

		setTypeFilter(filterOptions);
	}, [defaultAssets, assets]);

	useEffect(() => {
		if (searchCriteria) {
			let results = defaultAssets?.filter((asset) => {
				let hasType = true;
				let hasText = true;
				if (searchCriteria.text && searchCriteria.text.length > 0) {
					hasText = asset.measurementInfraestructureName
						.toLowerCase()
						.includes(searchCriteria.text.trim().toLowerCase());
				}
				if (searchCriteria.assetType && searchCriteria.assetType.key !== '0') {
					hasType =
						asset.infrastructureTypeName.trim().toLowerCase() ===
						searchCriteria.assetType.label.trim().toLowerCase();
				}
				return hasText && hasType;
			});
			setFilteredAssets(results ?? []);
		} else {
			setFilteredAssets(defaultAssets);
		}
	}, [searchCriteria, defaultAssets]);

	let assetTextFilter = useCallback(
		(text) => {
			let filter: SearchCriteria = {
				text: text,
				assetType: searchCriteria?.assetType ?? null,
			};
			setSearchCriteria(filter);
		},
		[searchCriteria],
	);

	let assetTypeFilter = useCallback(
		(assetType) => {
			let filter: SearchCriteria = {
				text: searchCriteria?.text ?? null,
				assetType: assetType,
			};
			setSearchCriteria(filter);
		},
		[searchCriteria],
	);

	const closeAllModals = useCallback(() => {
		window.dispatchEvent(new CustomEvent('nwl:close-all-modals'));
	}, []);

	return (
		showComparisonGraph && (
			<div
				data-testid="comparison-graph-modal"
				id="comparison-graph-modal"
				tabIndex={-1}
				aria-hidden="true"
				style={{ backdropFilter: 'blur(2px)', backgroundColor: 'rgba(0, 0, 0, 0.4)' }}
				className="flex overflow-y-auto overflow-x-hidden fixed top-0 bottom-0 right-0 left-0 z-[102] justify-center items-center h-screen"
			>
				<div className="comparison-graph">
					<ModalHeader
						onModalClose={() => {
							setShowComparisonGraph(false);
							setColorMap({});
							closeAllModals();
						}}
					/>
					<div className="flex h-full w-full rounded-b-lg" style={{ clipPath: 'border-box' }}>
						<AssetListView
							maxSelected={6}
							unselectedAssets={filteredAssets}
							selectedAssets={selectedAssets}
							onAdd={async (asset, paremeterId) => {
								const selectionKey = `${String(getParentId(asset))}:${String(paremeterId)}`;

								if (pendingAdds[selectionKey]) return;
								setPendingAdds((prev) => ({ ...prev, [selectionKey]: true }));

								try {
									if (
										selectedAssets.some(
											(sa) =>
												`${String(getParentId(sa))}:${String(getParamId(sa))}` === selectionKey,
										)
									)
										return;

									const param =
										asset.measurementParameters?.find(
											(p: any) => getParamIdFromParameter(p) === paremeterId,
										) ??
										(asset.mainMeasurementTarget &&
										getParamIdFromParameter(asset.mainMeasurementTarget) === paremeterId
											? asset.mainMeasurementTarget
											: null);

									let fetchedUnit: string | undefined;
									try {
										fetchedUnit = param?.unit ?? asset?.mainMeasurementTarget?.unit ?? 'unknown';
									} catch {
										/* ignore, fallback below */
									}

									const measurementParameter = param
										? {
												...param,
												paremeterId: getParamIdFromParameter(param),
												paremeterTypeName: getParamLabelFromParameter(param),
												unit:
													fetchedUnit ??
													(param as any).unit ??
													asset?.mainMeasurementTarget?.unit ??
													'unknown',
											}
										: asset.measurementParameter;

									const newAsset = {
										...asset,
										id:
											asset.measurementeInfraestructureId ??
											asset.id ??
											asset.measurementInfraestructureId,
										name: asset.measurementInfraestructureName ?? asset.name ?? 'Unknown asset',
										measurementTarget: asset.mainMeasurementTarget ?? asset.measurementTarget,
										measurementParameter,
										type: asset.infrastructureTypeName ?? asset.type,
									};

									const nextFreeColorIdx = (taken: Set<number>) => {
										for (let i = 0; i < colorSet.length; i++) {
											if (!taken.has(i)) return i;
										}
										return 0;
									};

									let assignedIdx = colorMap[selectionKey];
									if (assignedIdx === undefined) {
										const taken = new Set<number>(
											selectedAssets
												.map((sa) => sa._colorIndex)
												.filter((n): n is number => typeof n === 'number'),
										);
										assignedIdx = nextFreeColorIdx(taken);
										setColorMap((prev) => ({ ...prev, [selectionKey]: assignedIdx! }));
									}

									const colouredAsset = { ...newAsset, _colorIndex: assignedIdx! };
									setSelectedAssets((prev) => [...prev, colouredAsset]);
								} finally {
									setPendingAdds((prev) => {
										const { [selectionKey]: _, ...rest } = prev;
										return rest;
									});
								}
							}}
							onRemove={(asset, paremeterId) => {
								const selectionKey = `${String(getParentId(asset))}:${String(paremeterId)}`;

								// remove the table row
								setSelectedAssets((prev) => {
									const next = prev.filter(
										(sa) => `${String(getParentId(sa))}:${String(getParamId(sa))}` !== selectionKey,
									);
									// free up the colour
									setColorMap((prevColors) => {
										const { [selectionKey]: _, ...rest } = prevColors;
										return rest;
									});
									return next;
								});
							}}
							onSearch={assetTextFilter}
							filterOptions={typeFilter}
							searchCriteria={searchCriteria}
							onFilterSelected={assetTypeFilter}
						/>
						<GraphView
							dateRange={dateRange}
							setDateRange={setDateRange}
							yAxis={yAxis}
							series={series}
							tableData={selectedAssets}
							selectedAssets={selectedAssets}
							selectedAssetItem={selectedAssetItem}
							chartKey={chartKey}
							onRemove={(asset, paremeterId) => {
								const selectionKey = `${String(getParentId(asset))}:${String(paremeterId)}`;
								// remove the table row
								setSelectedAssets((prev) => {
									const next = prev.filter(
										(sa) => `${String(getParentId(sa))}:${String(getParamId(sa))}` !== selectionKey,
									);
									// free up the colour
									setColorMap((prevColors) => {
										const { [selectionKey]: _, ...rest } = prevColors;
										return rest;
									});
									return next;
								});
							}}
							loading={graphLoading}
							error={graphError}
						/>
					</div>
				</div>
			</div>
		)
	);
};

export default ComparisonGraph;